"use client";

import { useState, ChangeEvent, FormEvent, useEffect } from "react";
import { X } from "lucide-react";

interface FormData {
  userName: string;
  userEmail: string;
  userPhone: string;
  userSubject: string;
  userMessage: string;
}

const Quote = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    userName: "",
    userEmail: "",
    userPhone: "",
    userSubject: "",
    userMessage: "",
  });

  // Determinar la URL base según el entorno
  const getBaseUrl = () => {
    if (typeof window !== 'undefined') {
      // Cliente: usar la misma URL del frontend para rutas relativas
      return '';
    }
    // Servidor: usar URL absoluta según entorno
    return process.env.NODE_ENV === 'development'
      ? 'http://172.16.254.68:3001'
      : 'https://siconsperu.com';
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const apiUrl = `${getBaseUrl()}/api/email/send-email`;
      
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
        credentials: 'same-origin' // Para manejar cookies si es necesario
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error en la solicitud');
      }

      const result = await response.json();
      
      if (result.success) {
        alert("Mensaje enviado correctamente");
        setIsOpen(false);
        setFormData({
          userName: "",
          userEmail: "",
          userPhone: "",
          userSubject: "",
          userMessage: "",
        });
      } else {
        throw new Error(result.message || "Error al procesar la respuesta");
      }
    } catch (error) {
      console.error("Error al enviar el mensaje:", error);
      alert(`Error al enviar el mensaje: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div>
      <button
        onClick={() => setIsOpen(true)}
        className="px-4 py-2 bg-green-accent text-white rounded-md hover:bg-green-light transition-colors duration-200"
        aria-label="Abrir formulario de cotización"
      >
        Cotizar
      </button>

      {isOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-[9999]">
          <div 
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setIsOpen(false)}
            style={{ backdropFilter: 'blur(2px)' }}
            aria-hidden="true"
          />
          
          <div 
            className="relative w-full max-w-md bg-white rounded-lg shadow-xl mx-4"
            onClick={handleModalClick}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
          >
            <div className="relative">
              <button
                onClick={() => setIsOpen(false)}
                className="absolute right-2 top-2 p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100 transition-colors duration-200"
                aria-label="Cerrar formulario"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form
              className="p-6 space-y-4"
              onSubmit={handleSubmit}
              noValidate
            >
              <h3 className="text-xl font-semibold text-center mb-4 text-green-dark pr-8" id="modal-title">
                ¡Solicita una cotización!
              </h3>
              <p className="text-center text-gray-700 text-sm">
                Completa el formulario y nos contactaremos contigo
              </p>

              <div>
                <label
                  htmlFor="userName"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="userName"
                  name="userName"
                  value={formData.userName}
                  onChange={handleChange}
                  required
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:ring-2 focus:ring-green-accent"
                  placeholder="Ingrese su nombre"
                  aria-required="true"
                />
              </div>
              <div>
                <label
                  htmlFor="userEmail"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="userEmail"
                  name="userEmail"
                  value={formData.userEmail}
                  onChange={handleChange}
                  required
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:ring-2 focus:ring-green-accent"
                  placeholder="Ingrese su email"
                  aria-required="true"
                />
              </div>
              <div>
                <label
                  htmlFor="userPhone"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  Teléfono
                </label>
                <input
                  type="tel"
                  id="userPhone"
                  name="userPhone"
                  value={formData.userPhone}
                  onChange={handleChange}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:ring-2 focus:ring-green-accent"
                  placeholder="Ingrese su teléfono"
                />
              </div>
              <div>
                <label
                  htmlFor="userSubject"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  Asunto
                </label>
                <input
                  type="text"
                  id="userSubject"
                  name="userSubject"
                  value={formData.userSubject}
                  onChange={handleChange}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:ring-2 focus:ring-green-accent"
                  placeholder="Ingrese el asunto"
                />
              </div>
              <div>
                <label
                  htmlFor="userMessage"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  Mensaje <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="userMessage"
                  name="userMessage"
                  rows={3}
                  value={formData.userMessage}
                  onChange={handleChange}
                  required
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:ring-2 focus:ring-green-accent"
                  placeholder="Ingrese su mensaje"
                  aria-required="true"
                />
              </div>

              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`px-6 py-2 bg-green-accent text-white rounded-md transition-colors duration-200 ${
                    isLoading 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:bg-green-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-accent'
                  }`}
                  aria-busy={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="inline-block animate-spin mr-2">↻</span>
                      Enviando...
                    </>
                  ) : 'Enviar Mensaje'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Quote;